import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cardApi, STATUS_STYLE } from "@/components/admin/TrainingskartenTab";
import { CodeReveal } from "@/components/admin/cards/CodeReveal";

const LEDGER_LABELS = {
  create: "Angelegt", payment_confirmed: "Zahlung bestätigt", invoice_created: "Rechnung erstellt",
  import_activate: "Nachtrag aktiviert", activation_email: "Karten-E-Mail", low_balance_email: "Guthaben-Erinnerung", reserve: "Reserviert",
  release: "Reservierung freigegeben", consume: "Eingelöst", refund: "Gutschrift (Storno)",
  forfeit: "Verfallen (späte Absage)", adjust: "Korrektur", deactivate: "Deaktiviert",
  reactivate: "Reaktiviert", converted_order: "Aus alter Bestellung übernommen", code_reissued: "Neuer Code",
};
const APT_STATUS = { pending: "Ausstehend", confirmed: "Bestätigt", rejected: "Abgelehnt", cancelled: "Storniert", completed: "Abgeschlossen" };
const UNIT_STATE = { reserved: "reserviert", consumed: "eingelöst", released: "freigegeben", refunded: "gutgeschrieben", forfeited: "verfallen" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { timeZone: "Europe/Berlin", dateStyle: "short", timeStyle: "short" }) : "");

const Ledger = ({ items }) => (
  <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="card-ledger">
    {items.map((e) => (
      <div key={e.entry_id} className="text-xs bg-[#0A0A0A] rounded p-2 flex justify-between gap-2">
        <span>
          <strong>{LEDGER_LABELS[e.type] || e.type}</strong>
          {e.appointment_id && ` · ${e.appointment_id}`}
          {e.delta_total != null && ` · ${e.delta_total > 0 ? "+" : ""}${e.delta_total}`}
          {e.reason && <span className="text-[#A1A1AA]"> · {e.reason}</span>}
          {e.test_suppressed && <span className="text-blue-400"> · Test, nicht gesendet</span>}
        </span>
        <span className="text-[#71717A] whitespace-nowrap">{fmt(e.at)} · {e.actor}</span>
      </div>
    ))}
  </div>
);

const ReasonAction = ({ label, needsDelta, onRun, testId, danger }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [delta, setDelta] = useState("");
  if (!open) return <Button size="sm" variant="outline" className={danger ? "border-red-500/40 text-red-400" : ""} onClick={() => setOpen(true)} data-testid={testId}>{label}</Button>;
  return (
    <div className="w-full flex flex-wrap gap-2 bg-white/5 rounded p-2">
      {needsDelta && <Input type="number" placeholder="+1 / -1" value={delta} onChange={(e) => setDelta(e.target.value)} className="w-24 bg-[#0A0A0A]" data-testid={`${testId}-delta`} />}
      <Input placeholder="Begründung (Pflicht)" value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1 min-w-[160px] bg-[#0A0A0A]" data-testid={`${testId}-reason`} />
      <Button size="sm" disabled={reason.trim().length < (needsDelta ? 5 : 3) || (needsDelta && !Number(delta))}
        onClick={async () => { await onRun(reason.trim(), Number(delta)); setOpen(false); setReason(""); setDelta(""); }} data-testid={`${testId}-submit`}>OK</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
    </div>
  );
};

export const CardDetailDialog = ({ cardId, adminToken, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(async () => {
    try { setD(await cardApi(adminToken, `/${cardId}`)); } catch (e) { toast.error(e.message); }
  }, [adminToken, cardId]);
  useEffect(() => { load(); }, [load]);

  const run = async (path, body, msg) => {
    try {
      const r = await cardApi(adminToken, `/${cardId}${path}`, "POST", body || {});
      if (r.code) setCode(r.code);
      toast.success(msg); await load(); onChanged();
    } catch (e) { toast.error(e.message); }
  };

  const c = d?.card;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="card-detail-dialog">
        <DialogHeader><DialogTitle className="font-mono">{cardId}</DialogTitle></DialogHeader>
        {!c ? <p className="text-sm text-[#71717A]">Lädt...</p> : (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[c.status]}`} data-testid="card-detail-status">{c.status_label}</span>
              <span>{c.package_name} – 60 Min. · {c.training_name}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[["Gesamt", c.credits_total], ["Genutzt", c.credits_used], ["Reserviert", c.credits_reserved], ["Verfügbar", c.credits_remaining]].map(([l, v]) => (
                <div key={l} className="bg-[#0A0A0A] rounded p-2"><p className="text-xs text-[#71717A]">{l}</p><p className="font-['Bebas_Neue'] text-2xl" data-testid={`card-detail-${l.toLowerCase()}`}>{v}</p></div>
              ))}
            </div>
            <div className="text-xs text-[#A1A1AA] space-y-1">
              <p>{c.customer.name} · {c.customer.email}{c.customer.phone && ` · ${c.customer.phone}`}</p>
              <p>Preis {c.price} € · {c.payment_status === "paid" ? `bezahlt am ${c.paid_at}` : "Zahlung offen"} · {c.valid_until ? `gültig bis ${c.valid_until}` : "noch nicht gültig"}</p>
              <p>Rechnung: {c.invoice_id || (c.source === "manual_import" ? "keine (Nachtrag)" : "–")} · Code: {c.code_hint || "noch keiner"}</p>
            </div>
            {code && <CodeReveal code={code} />}
            <div className="flex flex-wrap gap-2">
              {c.status === "unpaid" && (
                <Button size="sm" className="bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c]" data-testid="btn-card-confirm-payment"
                  onClick={() => window.confirm("Zahlungseingang bestätigen? Karte wird aktiviert, Rechnung erstellt und Code per E-Mail versendet.") && run("/confirm-payment", {}, "Zahlung bestätigt, Karte aktiv")}>
                  Zahlung bestätigen und Karte aktivieren
                </Button>
              )}
              {c.payment_status === "paid" && (
                <Button size="sm" variant="outline" data-testid="btn-card-reissue"
                  onClick={() => window.confirm("Neuen Code ausstellen? Der alte Code wird sofort ungültig.") && run("/reissue-code", { send_email: window.confirm("Neuen Code per E-Mail an den Kunden senden?") }, "Neuer Code ausgestellt")}>
                  Neuer Code
                </Button>
              )}
              <ReasonAction label="Guthaben korrigieren" needsDelta testId="btn-card-adjust" onRun={(reason, delta) => run("/adjust", { reason, delta }, "Guthaben korrigiert")} />
              {c.status === "deactivated"
                ? <ReasonAction label="Reaktivieren" testId="btn-card-reactivate" onRun={(reason) => run("/reactivate", { reason }, "Karte reaktiviert")} />
                : <ReasonAction label="Deaktivieren" danger testId="btn-card-deactivate" onRun={(reason) => run("/deactivate", { reason }, "Karte deaktiviert")} />}
            </div>
            <div>
              <p className="font-semibold mb-2">Buchungen mit dieser Karte</p>
              {d.appointments.length === 0 ? <p className="text-xs text-[#71717A]">Keine</p> : (
                <div className="space-y-1" data-testid="card-appointments">
                  {d.appointments.map((a) => (
                    <div key={a.appointment_id} className="text-xs bg-[#0A0A0A] rounded p-2 flex justify-between gap-2">
                      <span>{a.date} · {a.time}–{a.end_time} · {a.appointment_id}</span>
                      <span>{APT_STATUS[a.status] || a.status} · Einheit: {UNIT_STATE[a.card_state] || a.card_state}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div><p className="font-semibold mb-2">Verlauf</p><Ledger items={d.ledger} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
